import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGarantieMaxActions, IProductDataGM } from '../GarantieMax';
import { Row, Col, FormGroup, Form, FormControl, Button } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSummaryValidationSchemaGM } from '../validation-gm';
import { AppAlertUI, useAppAlertService } from 'App/components/utils/alerts/AppAlertService';
import { useWidgetService } from 'Services/widget';
import leftArrowIconUrl from 'assets/bf-images/left-arrow.svg';
import {
  ICreateLeadData,
  ILeadData,
  IPolicyObject,
  IPremiumCalculationStateGM,
  IProductDataForInvoice,
} from 'Services/widgets/interfaces';
import { ViewTariffSubStep1 } from '../ui/ViewTariffSubStep1';
import { ViewTariffResult } from '../ui/ViewTariffResult';
import { isObject } from '../../bf-hooks';
import {
  prepareDataForTariffGM,
  prepareAccountData,
  preparedDefaultGroupGM,
} from '../garantie-max-hooks';
import { ViewDocumentDownload } from '../ui/ViewDocumentDownload';
import { ViewPersonalStep2 } from '../ui/ViewPersonalStep2';
import { ViewPaymentStep3 } from '../ui/ViewPaymentStep3';
import { LoadingSpinner } from 'App/components/utils/LoadingSpinner';
import { IPersonalDataStepGM } from './PersonalDataStep';
import { GMBFStep } from 'App/components/widgets/garantie-max/enum';
import { IPaymentDataStep } from './PaymentStep';

export interface ISummaryStepGM {
  isAcceptedPrivacyPolicy: boolean | undefined;
  isAcceptedTermsAndConditions: boolean | undefined;
}

const initialData: ISummaryStepGM = {
  isAcceptedPrivacyPolicy: false,
  isAcceptedTermsAndConditions: false,
};

export interface ISummaryStepProps {
  productCode: string;
  lead: ILeadData;
  productData: IProductDataGM;
}

export const SummaryStep: React.FC<ISummaryStepProps> = ( { productCode, lead, productData } ) => {
  const { t } = useTranslation( [ 'widgets' ] );
  const { personalData, paymentData } = lead;
  const leadPersonalData = personalData as IPersonalDataStepGM;
  const actions = useGarantieMaxActions();
  const { showAlert } = useAppAlertService();
  const service = useWidgetService();
  const summaryValidationSchemaGM = useSummaryValidationSchemaGM();
  const [ tariffData, setTariffData ] = React.useState<IPremiumCalculationStateGM | null>( null );

  const formContext = useForm<ISummaryStepGM>( {
    defaultValues: initialData,
    mode: 'onChange',
    resolver: yupResolver( summaryValidationSchemaGM ),
  } );
  const { errors, control, formState } = formContext;
  const onSubmit = React.useCallback( async ( formData ) => {
    try {
      const tariff = { ...lead.subStep1TariffData };
      const preparedPolicyData = prepareDataForTariffGM(
        tariff,
        lead.subStep1DeviceItems!,
        productData.insuredObjects,
        false,
      );

      const preparedAccountData = prepareAccountData( leadPersonalData! );
      const subStepData = { ...lead.subStep2TariffData, ...formData };
      const preparedDefaultGroup = preparedDefaultGroupGM(
        subStepData,
        tariff,
        lead.personalData as IPersonalDataStepGM,
        lead.subStep1DeviceItems!,
        productData.insuredObjects,
        false,
      );

      const policyData: IPolicyObject[] = [ ...preparedPolicyData, ...preparedDefaultGroup ];

      const payloadLead: ICreateLeadData = {
        code: productData.leadData.leadCode,
        productCode: productCode,
        policyObjects: policyData,
        account: preparedAccountData,
      };

      const result = await service.createLead( payloadLead );

      if ( result ) {
        actions.onSubmit();
      } else {
        showAlert( {
          message: t( 'base:forms.messages.errorSave' ),
          type: 'danger',
        } );
      }
    } catch( e ) {
      if ( e instanceof Error ) {
        showAlert( {
          message: t( 'base:forms.messages.errorSave' ),
          type: 'danger',
        } );
      } else {
        throw e;
      }
    }
  }, [ actions, lead.personalData, lead.subStep1DeviceItems, lead.subStep1TariffData,
    lead.subStep2TariffData, leadPersonalData, productCode, productData.insuredObjects,
    productData.leadData.leadCode, service, showAlert, t ] );

  React.useEffect( () => {
    let isMounted = true;
    const loadTariff = async () => {
      try {
        const tariff = { ...lead.subStep1TariffData };
        const preparedData = prepareDataForTariffGM(
          tariff,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const subStepData = { ...lead.subStep2TariffData };
        const preparedDefaultGroup = preparedDefaultGroupGM(
          subStepData,
          tariff,
          lead.personalData as IPersonalDataStepGM,
          lead.subStep1DeviceItems!,
          productData.insuredObjects,
          false,
        );

        const sendData: IPolicyObject[] = [ ...preparedData, ...preparedDefaultGroup ];

        const payloadInvoice: IProductDataForInvoice = {
          policyObjects: sendData,
        };
        const result = await service.getProductInvoice( productCode, payloadInvoice );
        if ( isMounted ) {
          if ( result ) {
            setTariffData( {
              premiumData: result,
              error: null,
            } );
          } else {
            setTariffData( {
              premiumData: null,
              error: t( 'base:messages.noData' ),
            } );
          }
        }
      } catch( e ) {
        if ( e instanceof Error ) {
          if ( isMounted ) {
            setTariffData( {
              premiumData: null,
              error: `Error in premium calculation. The reason is: ${e.message}`,
            } );
          }
        } else {
          throw e;
        }
      }
    };
    loadTariff();
    return () => {
      isMounted = false;
    };
  }, [ service, productCode, t, lead.subStep1TariffData, lead.subStep2TariffData,
    productData, lead.subStep1DeviceItems, lead.personalData ] );

  if ( lead.paymentData === null ) {
    actions.goToStep( 'payment', false );
  }

  let personalZipCode = personalData?.zipCode;
  if ( isObject( personalZipCode ) && personalZipCode['name'] ) {
    personalZipCode = personalZipCode['name'];
  }

  if ( productData === null ) {
    return <LoadingSpinner />;
  }

  return (
    <Row className="mt-3" id="summary-step">
      <div id="alert-personal-page" className="max-w-450 row">
        <AppAlertUI />
      </div>
      <Col md={ 12 } className="sticky-top">
        <button
          id={ `${GMBFStep.SummaryStep}_back` }
          type="button"
          className="btn btn-link m-0 p-0 text-c-black f-16 back-button"
          onClick={ () => actions.goToStep( 'payment', false ) }
        >
          <img src={ leftArrowIconUrl } alt="Back" />
          <span className="back-text pl-3 align-middle">
            { t( 'bookingFunnel.previousBtn' ) }
          </span>
        </button>
      </Col>
      <Row className="max-w-450">
        <Col md={ 12 } className="head-tab text-center px-0">
          <h1 className="f-26 text-c-black">{ t( 'bookingFunnel.summary.stepTitle' ) }</h1>
        </Col>
        <Col md={ 12 } className="head-tab mt-4 px-0 text-center">
          <h2 className="f-18 text-c-black">{ t( 'bookingFunnel.summary.stepSubTitleGM' ) }</h2>
        </Col>
      </Row>
      <Row className="col-md-12 mx-0 mt-2 justify-content-center bf-custom-p-clear">
        <Col md={ 12 } className="mpx-0 mb-4">
          <ViewTariffResult
            tariffData={ tariffData! }
            lead={ lead }
            productData={ productData }
          />
        </Col>
        <Col className="bf-data-info mb-4 mpx-0" md={ 12 }>
          <ViewPersonalStep2
            personalZipCode={ personalZipCode }
            personalData={ leadPersonalData }
          />
          <ViewTariffSubStep1 lead={ lead } productData={ productData! } />
          <ViewPaymentStep3 paymentData={ paymentData as IPaymentDataStep } />
          <ViewDocumentDownload productCode={ productCode } lead={ lead } productData={ productData! } />
        </Col>
      </Row>
      <Row className="bf-summary-footer col-md-12 justify-content-center mt-3 mx-0 bf-custom-p-clear">
        <Col md={ 10 } className="mpx-0 mt-4">
          <Form noValidate onSubmit={ formContext.handleSubmit( onSubmit ) }>
            <Row>
              <Col sm={ 12 } className="mb-4">
                <Controller
                  name="isAcceptedPrivacyPolicy"
                  control={ control }
                  defaultValue={ initialData.isAcceptedPrivacyPolicy }
                  render={ ( props ) => (
                    <FormGroup controlId={ props.name }>
                      <div className="gm-checkbox-box">
                        <FormControl
                          { ...props }
                          className="form-check-input m-0 wd-24"
                          type="checkbox"
                          isInvalid={ errors?.[props.name] !== undefined }
                          onChange={ ( e: React.ChangeEvent<HTMLInputElement> ) =>
                            props.onChange( e.target.checked )
                          }
                          checked={ props.value }
                          onBlur={ props.onBlur }
                        />
                        <Form.Label className="f-16 checkbox-label">
                          { t( 'bookingFunnel.summary.acceptedPrivacyPolicy' ) }
                        </Form.Label>
                      </div>
                      <Form.Control
                        type="hidden"
                        isInvalid={ errors[props.name] !== undefined }
                      />
                      <Form.Control.Feedback type="invalid">
                        { t( 'bookingFunnel.summary.checkBoxErr' ) }
                      </Form.Control.Feedback>
                    </FormGroup>
                  ) }
                />
              </Col>
              <Col sm={ 12 } className="mb-4">
                <Controller
                  name="isAcceptedTermsAndConditions"
                  control={ control }
                  defaultValue={ initialData.isAcceptedTermsAndConditions }
                  render={ ( props ) => (
                    <FormGroup controlId={ props.name }>
                      <div className="gm-checkbox-box">
                        <FormControl
                          { ...props }
                          className="form-check-input m-0 wd-24"
                          type="checkbox"
                          isInvalid={ errors?.[props.name] !== undefined }
                          onChange={ ( e: React.ChangeEvent<HTMLInputElement> ) =>
                            props.onChange( e.target.checked )
                          }
                          checked={ props.value }
                          onBlur={ props.onBlur }
                        />
                        <Form.Label className="f-16 checkbox-label">
                          { t( 'bookingFunnel.summary.acceptedTermsAndConditions' ) }
                        </Form.Label>
                      </div>
                      <Form.Control
                        type="hidden"
                        isInvalid={ errors[props.name] !== undefined }
                      />
                      <Form.Control.Feedback type="invalid">
                        { t( 'bookingFunnel.summary.checkBoxErr' ) }
                      </Form.Control.Feedback>
                    </FormGroup>
                  ) }
                />
              </Col>
            </Row>
            <Row className="m-0 mt-5">
              <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0">
                <Button
                  id={ `${GMBFStep.SummaryStep}_next` }
                  variant="primary"
                  disabled={ formState.isSubmitting || !formState.isValid }
                  className="mr-0"
                  type="submit"
                >
                  { t( 'bookingFunnel.applyNowBtn' ) }
                </Button>
              </Col>
              <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0 mt-2">
                <Button
                  id={ `${GMBFStep.SummaryStep}_back` }
                  type="button"
                  variant="link"
                  className="mt-2 mb-2 mx-0 py-0 text-c-black custom-back-button"
                  onClick={ () => actions.goToStep( 'payment', false ) }
                >
                  { t( 'bookingFunnel.previousBtn' ) }
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Row>
  );
};
